// @flow

export type PasswordValidationResult = {
  isValid: boolean,
  errors: Array<string>,
  strength: string,
  score: number,
};

// Common weak passwords list
const COMMON_PASSWORDS = new Set([
  'password',
  '123456',
  '123456789',
  'qwerty',
  'abc123',
  'password123',
  'admin',
  'letmein',
  'welcome',
  'monkey',
  '1234567890',
  'qwertyuiop',
  'asdfghjkl',
  'zxcvbnm',
  'iloveyou',
  'princess',
  'football',
  'baseball',
  'basketball',
  'superman',
  'access',
  'master',
  'hello',
  'charlie',
  'aa12345678',
  '1234567',
  '555555',
  'lovely',
  '7777777',
  'welcome123',
  'dragon',
  'shadow',
  'ashley',
  'sunshine',
  'michael',
  'daniel',
  'computer',
  '121212',
  '131313',
  '123123',
  'batman',
  'trustno1',
]);

// Check for sequential characters
const hasSequentialChars = (password: string): boolean => {
  const lowerPassword = password.toLowerCase();
  const sequences = ['abcdefghijklmnopqrstuvwxyz', '0123456789', 'qwertyuiop', 'asdfghjkl', 'zxcvbnm'];

  for (const seq of sequences) {
    for (let i = 0; i <= seq.length - 3; i += 1) {
      const forward = seq.slice(i, i + 3);
      const backward = forward.split('').reverse().join('');
      if (lowerPassword.includes(forward) || lowerPassword.includes(backward)) {
        return true;
      }
    }
  }
  return false;
};

// Check for repeated characters
const hasRepeatedChars = (password: string): boolean => {
  if (password.length < 3) return false;

  let count = 1;
  for (let i = 1; i < password.length; i += 1) {
    if (password[i] === password[i - 1]) {
      count += 1;
      if (count >= 3) return true;
    } else {
      count = 1;
    }
  }
  return false;
};

export const validatePassword = (password: string, contextualStrings: Array<string> = []): PasswordValidationResult => {
  const result: PasswordValidationResult = {
    isValid: true,
    errors: [],
    strength: 'Weak',
    score: 0,
  };

  // Check minimum length
  if (password.length < 8) {
    result.isValid = false;
    result.errors.push('Password must be at least 8 characters long');
  } else {
    result.score += 10;
  }

  // Check for uppercase letter
  if (!/[A-Z]/.test(password)) {
    result.isValid = false;
    result.errors.push('Password must contain at least one uppercase letter');
  } else {
    result.score += 10;
  }

  // Check for lowercase letter
  if (!/[a-z]/.test(password)) {
    result.isValid = false;
    result.errors.push('Password must contain at least one lowercase letter');
  } else {
    result.score += 10;
  }

  // Check for digit
  if (!/\d/.test(password)) {
    result.isValid = false;
    result.errors.push('Password must contain at least one number');
  } else {
    result.score += 10;
  }

  // Check for special character
  if (!/[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(password)) {
    result.isValid = false;
    result.errors.push('Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)');
  } else {
    result.score += 10;
  }

  // Check against common passwords
  if (COMMON_PASSWORDS.has(password.toLowerCase())) {
    result.isValid = false;
    result.errors.push('Password is too common and easily guessable');
  } else {
    result.score += 15;
  }

  // Check for contextual strings
  contextualStrings.forEach((contextStr) => {
    if (contextStr && contextStr.length > 2) {
      if (password.toLowerCase().includes(contextStr.toLowerCase())) {
        result.isValid = false;
        result.errors.push(`Password must not contain '${contextStr}'`);
      }
      if (contextStr.toLowerCase().includes(password.toLowerCase())) {
        result.isValid = false;
        result.errors.push(`Password must not be contained within '${contextStr}'`);
      }
    }
  });

  // Check for sequential characters
  if (hasSequentialChars(password)) {
    result.score -= 10;
    result.errors.push('Password contains sequential characters which reduces security');
  }

  // Check for repeated characters
  if (hasRepeatedChars(password)) {
    result.score -= 5;
    result.errors.push('Password contains repeated characters which reduces security');
  }

  // Calculate strength based on score
  if (result.score >= 60) {
    result.strength = 'Strong';
  } else if (result.score >= 40) {
    result.strength = 'Medium';
  } else {
    result.strength = 'Weak';
  }

  return result;
};

export const isStrongPassword = (password: string, contextualStrings: Array<string> = []): boolean => {
  const result = validatePassword(password, contextualStrings);
  return result.isValid;
};

export const getPasswordStrengthColor = (strength: string): string => {
  switch (strength) {
    case 'Strong':
      return '#28a745'; // Green
    case 'Medium':
      return '#ffc107'; // Yellow
    case 'Weak':
    default:
      return '#dc3545'; // Red
  }
};

export const getPasswordRequirements = (): Array<string> => [
  'At least 8 characters long',
  'At least one uppercase letter (A-Z)',
  'At least one lowercase letter (a-z)',
  'At least one number (0-9)',
  'At least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)',
  'Must not be a common password',
  'Must not contain username or personal information',
  'Should not contain sequential characters (like abc, 123)',
  'Should not contain repeated characters (like aaa, 111)',
];
